import React, { useState } from 'react';
import { Mail, Phone, MapPin, Linkedin, Github, Send, Instagram } from 'lucide-react';
import { personalInfo } from '../data/portfolioData';

const C = {
  cyan: '#00f5ff', green: '#00ff88', purple: '#7b2fff',
  gray: '#4a6275', white: '#e8f4f8',
  border: 'rgba(0,245,255,0.12)',
  card: 'rgba(0,245,255,0.025)',
};

export default function Contact() {
  const [hov, setHov] = useState(null);
  const [formState, setFormState] = useState({ name: '', message: '' });

  const contactLinks = [
    { icon: Mail, label: 'Email', value: personalInfo.email, href: `mailto:${personalInfo.email}` },
    { icon: Phone, label: 'WhatsApp', value: personalInfo.phone, href: `https://wa.me/${personalInfo.whatsapp.replace(/\+/g, '')}` },
    { icon: MapPin, label: 'Location', value: personalInfo.location, href: '#' },
    { icon: Linkedin, label: 'LinkedIn', value: '/in/marshallvd', href: personalInfo.linkedin },
    { icon: Github, label: 'GitHub', value: '/marshallvd', href: personalInfo.github },
    { icon: Instagram, label: 'Instagram', value: '@marshallvd', href: personalInfo.instagram },
  ];

  const handleSendWhatsApp = () => {
    const text = `Halo Marshall!\n\nNama saya: ${formState.name}\n\n${formState.message}`;
    const waNumber = personalInfo.whatsapp.replace(/\+/g, '');
    const waUrl = `https://wa.me/${waNumber}?text=${encodeURIComponent(text)}`;
    window.open(waUrl, '_blank');
  };

  return (
    <section id="contact" style={{ background: 'rgba(4,8,16,0.8)' }}>
      <div style={{ marginBottom: 60 }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          fontFamily: "'Space Mono', monospace", fontSize: 10,
          letterSpacing: 4, textTransform: 'uppercase', color: C.cyan, marginBottom: 12,
        }}>
          <span style={{ width: 32, height: 1, background: C.cyan, display: 'inline-block' }} />
          Contact
        </div>
        <h2 style={{
          fontFamily: "'Orbitron', monospace",
          fontSize: 'clamp(26px, 4vw, 44px)',
          fontWeight: 700, color: C.white, letterSpacing: -1,
        }}>
          Let's <span style={{ color: C.cyan }}>Connect</span>
        </h2>
        <p style={{ fontSize: 16, color: C.gray, marginTop: 14, maxWidth: 520, lineHeight: 1.7 }}>
          Open to full-time roles, freelance projects, and AI collaboration opportunities.
          Drop a message and I'll get back to you.
        </p>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: 48, alignItems: 'start',
      }}>
        {/* Contact info */}
        <div>
          <div style={{ marginBottom: 32 }}>
            {contactLinks.map((link, i) => {
              const Icon = link.icon;
              const isHov = hov === i;
              const isIG = link.label === 'Instagram';
              const hoverColor = isIG ? '#E1306C' : C.cyan;
              return (
                <a key={i} href={link.href}
                  target={link.href.startsWith('http') ? '_blank' : undefined}
                  rel="noreferrer"
                  onMouseEnter={() => setHov(i)}
                  onMouseLeave={() => setHov(null)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 14,
                    padding: '16px 18px', marginBottom: 10,
                    background: isHov ? 'rgba(0,245,255,0.04)' : C.card,
                    border: `1px solid ${isHov ? (isIG ? '#E1306C44' : 'rgba(0,245,255,0.35)') : C.border}`,
                    textDecoration: 'none',
                    transition: 'all .25s',
                    transform: isHov ? 'translateX(5px)' : 'none',
                  }}
                >
                  <div style={{
                    width: 38, height: 38,
                    background: isHov ? (isIG ? 'rgba(225,48,108,0.12)' : 'rgba(0,245,255,0.12)') : 'rgba(0,245,255,0.05)',
                    border: `1px solid ${C.border}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0, transition: 'all .25s',
                  }}>
                    <Icon size={16} color={isHov ? hoverColor : C.gray} />
                  </div>
                  <div>
                    <div style={{
                      fontFamily: "'Space Mono', monospace", fontSize: 8,
                      letterSpacing: 2, color: isHov ? hoverColor : C.gray,
                      textTransform: 'uppercase', marginBottom: 2,
                      transition: 'color .25s',
                    }}>
                      {link.label}
                    </div>
                    <div style={{ fontSize: 13.5, color: C.white, letterSpacing: 0.3 }}>
                      {link.value}
                    </div>
                  </div>
                </a>
              );
            })}
          </div>

          <div style={{
            padding: '18px 20px',
            background: 'rgba(0,255,136,0.04)',
            border: '1px solid rgba(0,255,136,0.2)',
            display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: C.green, animation: 'pulse 1.5s ease-in-out infinite' }} />
            <div>
              <div style={{ fontSize: 13.5, color: C.white, fontWeight: 600, marginBottom: 2 }}>
                Currently Available
              </div>
              <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: C.gray, letterSpacing: 1.5 }}>
                Open to opportunities · Remote-friendly
              </div>
            </div>
          </div>
        </div>

        {/* Message form → WhatsApp */}
        <div style={{
          background: C.card, border: `1px solid ${C.border}`,
          padding: 32,
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8,
          }}>
            <div style={{ fontSize: 22 }}>💬</div>
            <div style={{
              fontFamily: "'Orbitron', monospace",
              fontSize: 14, fontWeight: 600, color: C.white,
              letterSpacing: 1,
            }}>
              Send via WhatsApp
            </div>
          </div>
          <p style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: C.gray, letterSpacing: 1.5, marginBottom: 24, textTransform: 'uppercase' }}>
            Message will open in WhatsApp
          </p>

          <div style={{ marginBottom: 16 }}>
            <label style={{
              display: 'block',
              fontFamily: "'Space Mono', monospace", fontSize: 9,
              letterSpacing: 2, color: C.gray, textTransform: 'uppercase',
              marginBottom: 8,
            }}>
              Your Name
            </label>
            <input
              type="text"
              value={formState.name}
              onChange={e => setFormState(prev => ({ ...prev, name: e.target.value }))}
              placeholder="John Doe"
              style={{
                width: '100%', padding: '12px 16px',
                background: 'rgba(0,0,0,0.4)',
                border: `1px solid ${C.border}`,
                color: C.white, fontSize: 14,
                fontFamily: "'Rajdhani', sans-serif",
                outline: 'none',
                transition: 'border-color .2s',
                boxSizing: 'border-box',
              }}
              onFocus={e => e.target.style.borderColor = 'rgba(0,245,255,0.5)'}
              onBlur={e => e.target.style.borderColor = C.border}
            />
          </div>

          <div style={{ marginBottom: 24 }}>
            <label style={{
              display: 'block',
              fontFamily: "'Space Mono', monospace", fontSize: 9,
              letterSpacing: 2, color: C.gray, textTransform: 'uppercase',
              marginBottom: 8,
            }}>
              Message
            </label>
            <textarea
              rows={5}
              value={formState.message}
              onChange={e => setFormState(prev => ({ ...prev, message: e.target.value }))}
              placeholder="Tell me about your project or opportunity..."
              style={{
                width: '100%', padding: '12px 16px',
                background: 'rgba(0,0,0,0.4)',
                border: `1px solid ${C.border}`,
                color: C.white, fontSize: 14,
                fontFamily: "'Rajdhani', sans-serif",
                outline: 'none', resize: 'vertical',
                transition: 'border-color .2s',
                boxSizing: 'border-box',
              }}
              onFocus={e => e.target.style.borderColor = 'rgba(0,245,255,0.5)'}
              onBlur={e => e.target.style.borderColor = C.border}
            />
          </div>

          <button
            onClick={handleSendWhatsApp}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
              width: '100%', padding: '13px',
              background: `linear-gradient(135deg, #25D366, #128C7E)`,
              color: '#fff', fontFamily: "'Space Mono', monospace",
              fontSize: 10, letterSpacing: 2, textTransform: 'uppercase',
              fontWeight: 700, cursor: 'pointer', border: 'none',
              transition: 'all .3s',
            }}
            onMouseEnter={e => e.currentTarget.style.boxShadow = '0 0 40px rgba(37,211,102,0.4)'}
            onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
          >
            <Send size={14} /> Send via WhatsApp
          </button>
        </div>
      </div>
    </section>
  );
}
